import React from "react"
import { Table, Tooltip } from "components/Core"
import { HiOutlineEye, HiOutlineSearch, HiClipboardCopy, HiOutlineClipboardList, HiOutlineClock, HiOutlineUser, HiOutlineTerminal, HiSortAscending, HiSortDescending, HiOutlineDocumentDuplicate } from "react-icons/hi"
import { format_datetime_by_local_client } from "helpers/datetime_helper"
import ColumnFilter from "./ColumnFilter"

interface SeriesTableContentProps {
  columns: string[]
  current_data: any[]
  data_table_items: any[]
  loading_app: boolean
  skuSearch: string
  setSkuSearch: (v: string) => void
  serieSapSearch: string
  setSerieSapSearch: (v: string) => void
  estadoOmnixSearch: any[]
  setEstadoOmnixSearch: (v: any[]) => void
  isAvailableSearch: boolean | null
  setIsAvailableSearch: (v: boolean | null) => void
  openFilterColumn: string | null
  setOpenFilterColumn: (v: string | null) => void
  apply_filter: (v: any) => void
  remove_filter: (id: string) => void
  onPreview: (item: any) => void
  onProductPreview: (sku: string) => void
  onStoresModalOpen: (stores: any[]) => void
  onRelatedOrdersPreview: (item: any) => void
  onHistoryPreview: (item: any) => void
  onRequestStateChange?: (serie: any, newStateId: number, newStateLabel: string) => void
  search_error?: any
  refreshSearch?: () => void
  sortField?: string
  sortOrder?: "asc" | "desc"
  handleSort?: (field: string) => void
  onClone?: (item: any) => void
}

const SeriesTableContent: React.FC<SeriesTableContentProps> = ({
  columns,
  current_data,
  data_table_items,
  loading_app,
  skuSearch,
  setSkuSearch,
  serieSapSearch,
  setSerieSapSearch,
  estadoOmnixSearch,
  setEstadoOmnixSearch,
  isAvailableSearch,
  setIsAvailableSearch,
  openFilterColumn,
  setOpenFilterColumn,
  apply_filter,
  remove_filter,
  onPreview,
  onProductPreview,
  onStoresModalOpen,
  onRelatedOrdersPreview,
  onHistoryPreview,
  onRequestStateChange,
  search_error,
  refreshSearch,
  sortField,
  sortOrder,
  handleSort,
  onClone,
}) => {
  return (
    <Table className="!border-none !overflow-visible !drop-shadow-none">
      <Table.Head className="border-b border-gray-100">
        <Table.HeadCell 
          className="sticky top-0 !bg-gray-50/95 backdrop-blur-md !text-gray-400 !font-black !text-[11px] md:!text-[12px] !uppercase !tracking-widest !py-5 !text-center w-[80px] border-b border-gray-100 shadow-sm"
          style={{ zIndex: 9 }}
        >
          Acciones
        </Table.HeadCell>
        {columns.map((key: string, idx: number) => (
          <Table.HeadCell
            key={idx}
            className="sticky top-0 !bg-gray-50/95 backdrop-blur-md !text-gray-400 !font-black !text-[11px] md:!text-[12px] !uppercase !tracking-widest !py-5 min-w-[140px] border-b border-gray-100 shadow-sm transition-all"
            style={{ zIndex: 8 - idx }}
          >
            <div className="flex flex-col py-1 min-w-[max-content] relative">
              <div className="flex flex-row items-center justify-between gap-4 flex-nowrap whitespace-nowrap relative z-10">
                <div 
                  className={`flex items-center gap-1.5 ${key === "F. Creación" && handleSort ? "cursor-pointer hover:text-gray-600 transition-colors" : ""}`}
                  onClick={() => {
                    if (handleSort && key === "F. Creación") {
                      handleSort("_omnixStateMachine.currentState.date")
                    }
                  }}
                >
                  <span className="font-black">{key}</span>
                  {key === "F. Creación" && (
                    <div className="flex items-center justify-center p-0.5 rounded-md hover:bg-gray-100 transition-colors">
                      {sortField === "_omnixStateMachine.currentState.date" ? (
                        sortOrder === "asc" ? <HiSortAscending className="text-sm text-indigo-500" /> : <HiSortDescending className="text-sm text-indigo-500" />
                      ) : (
                        <HiSortDescending className="text-sm text-gray-300" />
                      )}
                    </div>
                  )}
                </div>
                {(key === "Sku" || key === "Serie SAP" || key === "Estado Omnix" || key === "Disponible") && (
                  <ColumnFilter
                    columnKey={key}
                    isOpen={openFilterColumn === key}
                    onToggle={setOpenFilterColumn}
                    searchValue={key === "Sku" ? skuSearch : key === "Serie SAP" ? serieSapSearch : key === "Disponible" ? isAvailableSearch : ""}
                    setSearchValue={key === "Sku" ? setSkuSearch : key === "Serie SAP" ? setSerieSapSearch : setIsAvailableSearch}
                    onApply={apply_filter}
                    onRemove={remove_filter}
                    estadoOmnixSearch={estadoOmnixSearch}
                    setEstadoOmnixSearch={setEstadoOmnixSearch}
                    isAvailableSearch={isAvailableSearch}
                    setIsAvailableSearch={setIsAvailableSearch}
                    onForceSearch={refreshSearch}
                  />
                )}
              </div>
            </div>
          </Table.HeadCell>
        ))}
      </Table.Head>
      <Table.Body className="!divide-none !border-none">
        {data_table_items?.length ? (
          data_table_items.map((item: any, i: number) => (
            <Table.Row key={i} className="group hover:bg-[#F2F2F7] transition-colors relative border-b border-gray-100">
              <Table.Cell className="flex flex-row items-center justify-center gap-3 !border-l-0 !bg-transparent transition-colors">
                <Tooltip content="Vista rápida" align="top">
                  <button onClick={() => onPreview(current_data[i])}>
                    <HiOutlineEye className="text-[17px] text-indigo-500 cursor-pointer hover:scale-110 transition-transform" />
                  </button>
                </Tooltip>
                <Tooltip content="Ver órdenes relacionadas" align="top">
                  <button onClick={() => onRelatedOrdersPreview(current_data[i])}>
                    <HiOutlineClipboardList className="text-[17px] text-teal-600 cursor-pointer hover:scale-110 transition-transform" />
                  </button>
                </Tooltip>
                <Tooltip content="Ver historial transaccional" align="top">
                  <button onClick={() => onHistoryPreview(current_data[i])}>
                    <HiOutlineClock className="text-[17px] text-blue-500 cursor-pointer hover:scale-110 transition-transform" />
                  </button>
                </Tooltip>
                {onClone && (
                  <Tooltip content="Clonar serie" align="top">
                    <button onClick={() => onClone(current_data[i])}>
                      <HiOutlineDocumentDuplicate className="text-[17px] text-rose-500 cursor-pointer hover:scale-110 transition-transform" />
                    </button>
                  </Tooltip>
                )}
              </Table.Cell>
              {Object.entries(item || {}).map(([key, value]: [string, any], j: number) => (
                <Table.Cell key={j} className="!border-l-0 !bg-transparent !text-gray-600 transition-colors">
                  <div className="flex items-center gap-2 group/copy whitespace-nowrap">
                    {key === "sku" || key === "serieSap" ? (
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          if (key === "sku") {
                            onProductPreview(String(value))
                          } else {
                            onPreview(current_data[i])
                          }
                        }}
                        className="text-indigo-600 hover:text-indigo-800 font-bold hover:underline bg-transparent transition-colors flex items-center gap-1.5"
                      >
                        {value as any}
                      </button>
                    ) : key === "tienda" ? (
                      Array.isArray(value) && value.length > 1 ? (
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onStoresModalOpen(value)
                          }}
                          className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase border border-blue-100 hover:bg-blue-100 transition-colors"
                        >
                          Ver tiendas ({value.length})
                        </button>
                      ) : (
                        <span className="font-medium text-gray-500">
                          {Array.isArray(value) && value.length === 1
                            ? `${value[0].id} (${value[0].name})`
                            : (value as any)}
                        </span>
                      )
                    ) : key === "estadoOmnix" ? (
                      <select
                        className="text-[12px] font-semibold text-gray-700 bg-gray-50/50 border border-transparent rounded-lg py-1 pl-2 pr-6 hover:bg-white hover:border-gray-300 hover:shadow-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M7%209L12%2014L17%209%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_2px_center] bg-[length:16px]"
                        value={String(value).split(" - ")[0]} // expects like "0" or "1"
                        onChange={(e) => {
                          if (onRequestStateChange) {
                            const newId = parseInt(e.target.value)
                            const newLabel = e.target.options[e.target.selectedIndex].text
                            onRequestStateChange(current_data[i], newId, newLabel)
                          }
                        }}
                      >
                        <option value="0">0 - Libre</option>
                        <option value="1">1 - Vendido</option>
                        <option value="4">4 - Reservado</option>
                        <option value="6">6 - Eliminado</option>
                        <option value="7">7 - Bloqueado</option>
                      </select>
                    ) : key === "ultimaTransaccion" ? (
                      value ? (
                        <Tooltip content={`${value.user || "system@omnix"}: ${(() => {
                          const dateStr = value.timestamp || value.createdAt || value.updatedAt || value.date;
                          return dateStr ? format_datetime_by_local_client(dateStr, "dd/MM/yyyy HH:mm:ss") : "Error";
                        })()}`} align="top">
                          <div className="flex items-center gap-2.5 bg-white/40 border border-gray-100 rounded-lg p-1 pr-3 shadow-sm hover:shadow-md transition-all duration-200 group/tx max-w-[170px]">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                              value.user?.includes("system") || !value.user ? "bg-slate-100 text-slate-500" : "bg-indigo-50 text-indigo-500"
                            }`}>
                              {value.user?.includes("system") || !value.user ? (
                                <HiOutlineTerminal className="text-[14px]" />
                              ) : (
                                <HiOutlineUser className="text-[14px]" />
                              )}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="text-[11px] font-bold text-gray-800 truncate leading-tight">
                                {value.user || "system@omnix"}
                              </span>
                              <span className="text-[9px] font-medium text-gray-400 leading-tight">
                                {(() => {
                                  const dateStr = value.timestamp || value.createdAt || value.updatedAt || value.date;
                                  return dateStr ? format_datetime_by_local_client(dateStr, "dd MMM, HH:mm") : "--";
                                })()}
                              </span>
                            </div>
                          </div>
                        </Tooltip>
                      ) : (
                        <div className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50/50 border border-dashed border-gray-200 rounded-lg w-fit">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse" />
                          <span className="text-[10px] font-medium text-gray-400 italic">
                            Sin registros
                          </span>
                        </div>
                      )
                    ) : (key === "fechaCreacion" || key === "fechaActualizacion") ? (
                      value ? (
                        <span className="text-[12.5px] font-medium text-gray-600">
                          {format_datetime_by_local_client(new Date(parseInt(value as string)), "dd/MM/yyyy HH:mm")}
                        </span>
                      ) : (
                        <span className="text-[12px] italic text-gray-400">---</span>
                      )
                    ) : (
                      <span className="text-[13px]">{value as any}</span>
                    )}

                    {(key === "sku" || key === "serieSap") && value && (
                      <Tooltip content={`Copiar ${key === "sku" ? "SKU" : "Serie"}`} align="top">
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            navigator.clipboard.writeText(String(value))
                          }}
                          className="p-1 px-1.5 opacity-0 group-hover/copy:opacity-100 bg-gray-50 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-100 rounded-md transition-all duration-200"
                        >
                          <HiClipboardCopy className="text-[13px]" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </Table.Cell>
              ))}
            </Table.Row>
          ))
        ) : (
          <Table.Row className="!border-none">
            <Table.Cell colSpan={columns.length + 1} className="py-16 text-center !bg-transparent !border-none">
              <div className="flex flex-col items-center justify-center gap-3">
                {loading_app ? (
                  <div className="flex items-center gap-3">
                    <span className="h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
                    <span className="text-gray-500 font-medium">Cargando series...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center max-w-[450px]">
                    <div className="w-16 h-16 bg-indigo-50/50 rounded-full flex items-center justify-center mb-4">
                      <HiOutlineSearch className="text-3xl text-indigo-400" />
                    </div>
                    <span className="text-[15px] font-black text-gray-800 uppercase tracking-tight mb-1">
                      Sin resultados disponibles
                    </span>
                    <span className="text-[12px] text-gray-400 font-medium text-center leading-relaxed">
                      {search_error?._detail || (
                        <>
                          No encontramos coincidencias para los filtros aplicados. <br />
                          Intenta ajustar tu búsqueda o limpiar los filtros.
                        </>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  )
}

export default SeriesTableContent
